import { createContext, PropsWithChildren, useContext, useMemo, useRef } from 'react';

import { lookupApi } from 'services/lookup/lookup';
import { myAccountApi } from 'services/myAccount/myAccount';
import { programsApi } from 'services/programs/programs';
import { surveysApi } from 'services/surveys/surveys';

import { selectUser } from 'store';

import { useAppSelector } from 'hooks';

export interface BootstrapQueryStatus {
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isUninitialized: boolean;
}

export interface AuthBootstrapContextValue {
  isBootstrapped: boolean;
  membershipPlans: BootstrapQueryStatus;
  myAccount: BootstrapQueryStatus;
  myPrograms: BootstrapQueryStatus;
  surveys: BootstrapQueryStatus;
}

const defaultStatus: BootstrapQueryStatus = {
  isError: false,
  isFetching: false,
  isSuccess: false,
  isUninitialized: true
};

const defaultValue: AuthBootstrapContextValue = {
  isBootstrapped: false,
  membershipPlans: defaultStatus,
  myAccount: defaultStatus,
  myPrograms: defaultStatus,
  surveys: defaultStatus
};

export const AuthBootstrapContext = createContext<AuthBootstrapContextValue>(defaultValue);

export const useAuthBootstrapStatus = () => useContext(AuthBootstrapContext);

const toStatus = (state: {
  isError?: boolean;
  isFetching?: boolean;
  isSuccess?: boolean;
  isUninitialized?: boolean;
}): BootstrapQueryStatus => ({
  isError: !!state.isError,
  isFetching: !!state.isFetching,
  isSuccess: !!state.isSuccess,
  isUninitialized: state.isUninitialized ?? true
});

/**
 * A query is settled once it has been triggered and has come back, successfully
 * or not. An error counts: callers need to stop waiting and act on what they
 * have, rather than block forever on a fetch that will not arrive.
 *
 * A refetch makes this false again, and that is deliberate. Every caller that
 * reads it — `RequireProgram`, `Router`, `isBootstrapped` — acts *against* the
 * patient when the answer is no, and `useMyPrograms` is one response behind for
 * the whole of a refetch: it reads the slice that `getMyPrograms.onQueryStarted`
 * writes only on fulfilment. A program missing from a stale slice is not a
 * program the patient does not have. Guards that must not re-open a decision
 * they have already made want `useBootstrapQuerySettledOnce` below, not a wider
 * definition of settled here.
 */
export const isBootstrapQuerySettled = (s: BootstrapQueryStatus) =>
  !s.isUninitialized && !s.isFetching && (s.isSuccess || s.isError);

/**
 * `isBootstrapQuerySettled`, latched the first time it is true.
 *
 * For the guards whose own child triggers the next fetch. `<PostOnboarding />`
 * fires an argless `getMyPrograms()` from `useEffect(onInit, [])`, which is the
 * very cache entry the guard above it watches — so the guard replaced its child
 * with a loader because that child asked a question, remounted it when the
 * answer arrived, and went round again at roughly four cycles a second
 * (LMD-2942). Re-deciding on every render is the defect; the fetch is not.
 *
 * Latching fixes that without widening `isBootstrapQuerySettled` for the callers
 * that still need its stricter meaning. The *first* settle still requires a
 * quiet query, so a guard that has not decided yet keeps waiting — which is what
 * keeps a weight-management patient out of the legacy container while her
 * program sits in a refetch that has not landed (LMD-2794). Once a guard has
 * rendered its child, no later refetch can take it away.
 *
 * A ref rather than state, so it cannot schedule a render of its own; `||=` is
 * idempotent, so a StrictMode double-invoke lands on the same value. It resets
 * on unmount, which is right: a guard mounting again is deciding again, and a
 * decision carried across mounts would be the stale read this avoids.
 */
export const useBootstrapQuerySettledOnce = (status: BootstrapQueryStatus) => {
  const hasSettled = useRef(false);
  hasSettled.current ||= isBootstrapQuerySettled(status);
  return hasSettled.current;
};

/**
 * Observes the post-auth fetches that other parts of the app (App.tsx's bootstrap
 * effect) trigger when accessToken becomes truthy, and exposes a single
 * `isBootstrapped` flag that consumers can wait on before navigating into
 * auth-gated routes. Surveys is intentionally excluded from the gate because it
 * is only fetched when the user has programs, so a brand-new user would never
 * fire it and the gate would stay closed.
 */
export const AuthBootstrapProvider = ({ children }: PropsWithChildren) => {
  const { accessToken } = useAppSelector(selectUser);

  const myAccountState = myAccountApi.endpoints.getMyAccount.useQueryState(undefined);
  const myProgramsState = programsApi.endpoints.getMyPrograms.useQueryState(undefined);
  const membershipPlansState = lookupApi.endpoints.getMembershipPlans.useQueryState(undefined);
  const surveysState = surveysApi.endpoints.getSurveys.useQueryState(undefined);

  const value = useMemo<AuthBootstrapContextValue>(() => {
    const myAccount = toStatus(myAccountState);
    const myPrograms = toStatus(myProgramsState);
    const membershipPlans = toStatus(membershipPlansState);
    const surveys = toStatus(surveysState);
    const isBootstrapped =
      !!accessToken &&
      isBootstrapQuerySettled(myAccount) &&
      isBootstrapQuerySettled(myPrograms) &&
      isBootstrapQuerySettled(membershipPlans);
    return { isBootstrapped, membershipPlans, myAccount, myPrograms, surveys };
  }, [accessToken, myAccountState, myProgramsState, membershipPlansState, surveysState]);

  return <AuthBootstrapContext.Provider value={value}>{children}</AuthBootstrapContext.Provider>;
};
